import { ICourseWithSections } from '@sdk/contracts';
import { TreeSelect } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllCoursesWithSectionsByTags } from '@/store';

type Props = {
  value?: ICourseWithSections[];
  onChange?: (value: ICourseWithSections[]) => void;
  tag?: string;
};

const { SHOW_PARENT } = TreeSelect;

export const CourseWithSectionsSelector: React.FC<Props> = ({ value, onChange, tag }) => {
  const { t } = useTranslation();
  const { courses = [] } = useAllCoursesWithSectionsByTags(tag ? [tag] : undefined);

  const treeValue = useMemo(() => {
    return value?.flatMap(v =>
      v?.sections?.length && v?.sections?.length > 0 ? v?.sections?.map(s => s._id) : v?.course._id
    );
  }, [value]);

  const treeData = useMemo(() => {
    return courses.map(course => {
      return {
        title: course.name,
        value: course._id,
        key: course._id,
        children: course.sections.map(section => ({
          title: section.name,
          value: section._id,
          key: section._id
        }))
      };
    });
  }, [courses]);

  const onChangeTree = (selectedKeys: string[]) => {
    if (!onChange) return;

    const selectedCourses: ICourseWithSections[] = [];

    selectedKeys.forEach(key => {
      const course = courses.find(c => c._id === key);
      if (course) {
        selectedCourses.push({
          course,
          sections: []
        });
      } else {
        const parentCourse = courses.find(c => c.sections.some(s => s._id === key));
        if (parentCourse) {
          const existingCourse = selectedCourses.find(sc => sc.course._id === parentCourse._id);
          if (existingCourse) {
            const section = parentCourse.sections.find(s => s._id === key);
            if (section) {
              existingCourse.sections?.push(section);
            }
          } else {
            const section = parentCourse.sections.find(s => s._id === key);
            if (section) {
              selectedCourses.push({
                course: parentCourse,
                sections: [section]
              });
            }
          }
        }
      }
    });

    onChange(selectedCourses);
  };

  return (
    <TreeSelect
      disabled={!tag}
      treeData={treeData}
      value={treeValue}
      onChange={onChangeTree}
      style={{ width: '100%', maxWidth: 250 }}
      treeCheckable
      showCheckedStrategy={SHOW_PARENT}
      placeholder={t('SELECT_ONE_OPTION')}
      maxTagCount="responsive"
      filterTreeNode={(input, option) =>
        (option?.title?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
      }
    />
  );
};
